import * as React from 'react';

export interface IWizardPanelProps<PANELKEY extends string> {
  changePanel: (panelKey: PANELKEY) => void;
  goBack: () => void;
}

export class WizardPanel<PANELKEY extends string = null, T1 = {}, T2 = {}> extends React.Component<IWizardPanelProps<PANELKEY> & T1, T2> {
  constructor(props: IWizardPanelProps<PANELKEY> & T1) {
    super(props);
  }
}

type PanelMap<PANELKEY extends string> = Record<PANELKEY, React.ComponentType<IWizardPanelProps<PANELKEY>>>;

interface IWizardProps<PANELKEY extends string> {
  panelComponents: PanelMap<PANELKEY>;
  firstPanel: PANELKEY;
  onPanelChange?: (nextPanel: PANELKEY, lastPanel: PANELKEY) => void;
}

interface IWizardState<PANELKEY extends string> {
  currentPanel: PANELKEY;
  panelStack: PANELKEY[];
}

export class Wizard<PANELKEY extends string, T> extends React.Component<IWizardProps<PANELKEY> & T, IWizardState<PANELKEY>> {
  constructor(props: IWizardProps<PANELKEY> & T) {
    super(props);

    this.state = {
      panelStack: [],
      currentPanel: props.firstPanel
    };
  }

  private readonly containerRef = React.createRef<HTMLDivElement>();

  private scrollToTop() {
    const el = this.containerRef.current;
    if (el) {
      const scrollableParent = (el.closest('[class*="ModalBody"]') || el.parentElement) as HTMLElement | null;
      if (scrollableParent) {
        scrollableParent.scrollTop = 0;
      }
    }
  }

  changePanel = (panelKey: PANELKEY) => {
    const { currentPanel, panelStack } = this.state;
    if (this.props.onPanelChange) {
      this.props.onPanelChange(panelKey, currentPanel);
    }
    const newStack = [...panelStack];
    newStack.push(currentPanel);
    this.setState({ currentPanel: panelKey, panelStack: newStack }, () => this.scrollToTop());
  };

  goBack = () => {
    const { currentPanel, panelStack } = this.state;
    if (panelStack.length === 0) {
      throw Error("Can't go back any further.");
    }
    const newStack = [...panelStack];
    const lastPanel = newStack.pop();

    if (this.props.onPanelChange) {
      this.props.onPanelChange(lastPanel, currentPanel);
    }
    this.setState({ currentPanel: lastPanel, panelStack: newStack }, () => this.scrollToTop());
  };

  render() {
    const panelComponents = this.props.panelComponents;
    const { currentPanel } = this.state;

    // todo: thomas -- there appears to be a bug with TypeScript where
    // indexing into the record doesn't correctly type Panel, below
    // ...hence the `as any as...` cast
    const Panel = panelComponents[currentPanel] as any as React.ComponentType<IWizardPanelProps<PANELKEY>>;

    const CurrentPanel = <div>{<Panel {...this.props} changePanel={this.changePanel} goBack={this.goBack} />}</div>;

    return <div ref={this.containerRef}>{CurrentPanel}</div>;
  }
}
